import DOMPurify from 'dompurify';
import { ADELAIDE_TZ } from '../../../utils/dateUtils';
import { escapeHtml, sanitizeHtml } from '../../../utils/sanitizeHtml';

/**
 * Reply-snippet variables: {{firstName}} and friends, resolved when a snippet is
 * dropped into the composer.
 *
 * Everything here is pure (no Firestore, no React) so the rules that decide what
 * a client is greeted with are unit-tested in one place. The guiding rule: a
 * variable we cannot fill CONFIDENTLY stays visible as a literal {{name}} - a
 * wrong first name in a client email is worse than a visible gap the owner fills
 * in (and the composer refuses to send while a gap is left).
 */

// -- Variables ------------------------------------------------------------

export type SnippetVariable = 'firstName' | 'fullName' | 'customerName' | 'myFirstName' | 'today';

/** What the snippet form lists as insertable chips, and what the hint text explains. */
export const SNIPPET_VARIABLES: ReadonlyArray<{ name: SnippetVariable; hint: string }> = [
  { name: 'firstName', hint: "the recipient's first name" },
  { name: 'fullName', hint: "the recipient's full name" },
  { name: 'customerName', hint: "the linked customer's name" },
  { name: 'myFirstName', hint: 'your first name' },
  { name: 'today', hint: "today's date, e.g. 30 September 2026" },
];

const CANONICAL: Record<string, SnippetVariable> = {};
SNIPPET_VARIABLES.forEach((v) => { CANONICAL[v.name.toLowerCase()] = v.name; });

const canonicalVariable = (raw: string): SnippetVariable | null => {
  const key = raw.toLowerCase();
  // Own keys only: a plain object also answers for "constructor", "__proto__" and
  // "toString", which would hand a function (or Object.prototype) to the resolver
  // as if it were a variable and print its source into the client's email.
  return Object.prototype.hasOwnProperty.call(CANONICAL, key) ? CANONICAL[key] : null;
};

/**
 * Anything between a pair of double braces: no braces inside, and no paragraph
 * break (the send guard turns block ends into a control char), so `{{` and `name}}`
 * in two different paragraphs are never read as one token.
 */
const TOKEN_PATTERN = '\\{\\{([^{}\\u0001]*)\\}\\}';
const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/;
const TOKEN_NAME_MAX = 40;

/**
 * What a `{{ inner }}` token is. A well-formed name is matched case-insensitively
 * with whitespace tolerated; a known one is reported in its canonical spelling.
 * Everything else that looks like a placeholder ("{{first name}}", "{{customer.name}}",
 * "{{ }}") is still a token - it is never filled, but it must be SEEN, because a
 * typo'd placeholder otherwise reaches the client as a literal mustache.
 */
function classifyToken(inner: string): { canon: SnippetVariable | null; name: string; wellFormed: boolean } {
  const t = inner.trim();
  if (IDENTIFIER_RE.test(t)) {
    const canon = canonicalVariable(t);
    return { canon, name: canon || t, wellFormed: true };
  }
  const flat = t.replace(/\s+/g, ' ') || ' ';
  return {
    canon: null,
    name: flat.length > TOKEN_NAME_MAX ? `${flat.slice(0, TOKEN_NAME_MAX - 3)}...` : flat,
    wellFormed: false,
  };
}

// -- Names ----------------------------------------------------------------

const LETTER = 'A-Za-z\\u00C0-\\u00D6\\u00D8-\\u00F6\\u00F8-\\u00FF';
const NAME_TOKEN_RE = new RegExp(`^[${LETTER}][${LETTER}'\\u2019-]*$`);

const HONORIFICS = ['mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'prof', 'professor', 'sir', 'madam', 'rev'];

// Joining words and group nouns: "Mr and Mrs Smith", "The Smith Family", "John and
// Mary Smith" are not one person, and greeting them as "And" / "The" / "John" is a
// confident wrong guess that neither the visible gap nor the send guard would catch.
const FUNCTION_WORDS = ['and', 'or', 'the', 'of', 'from', 'at', 'for', 'with', 'family', 'families', 'household', 'residents'];

// Lowercase surname particles, kept as typed ("Pieter van der Berg", "Maria de Souza").
const PARTICLES = ['de', 'del', 'della', 'di', 'da', 'van', 'von', 'der', 'den', 'ten', 'ter', 'la', 'le', 'du', 'dos', 'das', 'bin', 'ibn', 'al', 'el'];

// "John Smith Jr", "Smith, John, MBA": generational suffixes stay in the full name,
// credentials are dropped. Only unambiguous ones - "Ma" or "Ba" are real surnames.
const GENERATIONAL: { [key: string]: string } = { jr: 'Jr', sr: 'Sr', ii: 'II', iii: 'III', iv: 'IV' };
const CREDENTIALS = ['phd', 'mba', 'cpa', 'esq', 'md', 'dds', 'dvm', 'obe', 'cfp', 'pmp', 'bsc', 'msc', 'mbbs', 'llb', 'llm', 'faicd', 'fcpa', 'mp'];

// Words that mean "this display name is a business or a role mailbox, not a
// person". Deliberately broad: a missed guess costs a visible {{firstName}}, a
// false positive costs "Hi Accounts,".
const NOT_A_PERSON = [
  'pty', 'ltd', 'limited', 'inc', 'llc', 'corp', 'corporation', 'co', 'company', 'group', 'holdings', 'trust',
  'team', 'support', 'accounts', 'account', 'admin', 'administrator', 'sales', 'office', 'services', 'service',
  'solutions', 'enquiries', 'enquiry', 'inquiries', 'info', 'hello', 'contact', 'reception', 'billing', 'bookings',
  'booking', 'noreply', 'donotreply', 'mail', 'mailer', 'notifications', 'notification', 'system', 'alerts',
  'marketing', 'helpdesk', 'webmaster', 'postmaster', 'orders', 'payroll', 'hr', 'customer', 'customers',
  'general', 'manager', 'director', 'studio', 'agency', 'consulting', 'consultants', 'plumbing', 'electrical',
  'constructions', 'builders', 'building', 'properties', 'property', 'realty', 'partners', 'associates',
  'help', 'desk', 'web', 'website', 'design', 'development', 'digital', 'media', 'tech', 'technology',
  'technologies', 'software', 'systems', 'network', 'networks', 'communications', 'telecom', 'insurance',
  'finance', 'financial', 'bank', 'banking', 'credit', 'union', 'council', 'government', 'department',
  'university', 'college', 'school', 'hospital', 'clinic', 'centre', 'center', 'club', 'association', 'society',
  'foundation', 'church', 'hotel', 'restaurant', 'wholesale', 'retail', 'supplies', 'supply', 'logistics',
  'transport', 'freight', 'energy', 'electric', 'electricity', 'gas', 'water', 'power', 'post', 'ads',
  'advertising', 'google', 'microsoft', 'facebook', 'amazon', 'apple', 'paypal', 'stripe', 'xero', 'linkedin',
  'synergy', 'siteground', 'godaddy', 'zoom', 'canva', 'shopify', 'wix', 'australia', 'australian', 'construct',
  'construction', 'engineering', 'industries', 'enterprises', 'ventures', 'international', 'global', 'national',
  'institute', 'authority', 'commission', 'health', 'medical', 'dental', 'legal', 'lawyers', 'accounting',
  'accountants', 'bookkeeping',
];

/** "john" / "JOHN" -> "John", "mary-jane" -> "Mary-Jane"; mixed case (McDonald, DeAngelo) is left alone. */
function tidyNameToken(token: string): string {
  const letters = token.replace(/[^A-Za-zÀ-ÿ]/g, '');
  if (letters.length < 2) return token;
  const isLower = letters === letters.toLowerCase();
  const isUpper = letters === letters.toUpperCase();
  if (!isLower && !isUpper) return token;
  return token
    .toLowerCase()
    .replace(new RegExp(`(^|['\\u2019-])([${LETTER}])`, 'g'), (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

/** A run of suffix words ("Jr", "Ph.D", "MBA") and nothing else, as lowercase keys, or null. */
function suffixWords(text: string): string[] | null {
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length === 0) return null;
  const keys = words.map((w) => w.replace(/\./g, '').toLowerCase());
  const ok = keys.every((k) => Object.prototype.hasOwnProperty.call(GENERATIONAL, k) || CREDENTIALS.indexOf(k) >= 0);
  return ok ? keys : null;
}

/**
 * A display name we are willing to greet someone with, or null. Handles
 * `"Smith, John"` (Outlook's Last, First), quotes, a trailing `(Company)`,
 * honorifics and name suffixes; refuses anything that looks like an address, a
 * business, a group ("Mr and Mrs Smith") or a role.
 */
export function personName(raw: unknown): { first: string; full: string } | null {
  let s = String(raw ?? '').trim();
  if (!s) return null;
  s = s.replace(/^["'‘’“”]+|["'‘’“”]+$/g, '').trim();
  s = s.replace(/\s*\([^)]*\)\s*$/, '').trim(); // "John Smith (Acme)"
  if (!s || /[@<>{}[\]\\/|;:0-9]/.test(s)) return null;

  const parts = s.split(',').map((p) => p.trim());
  if (parts.some((p) => !p)) return null;
  let generational = '';
  const noteSuffixes = (keys: string[]) => keys.forEach((k) => {
    if (Object.prototype.hasOwnProperty.call(GENERATIONAL, k)) generational = GENERATIONAL[k];
  });
  // "Smith, Jr" / "John Smith, MBA": a suffix after a comma is not the other half
  // of "Last, First", so take it off BEFORE deciding whether this is Last, First.
  for (;;) {
    const tail = parts.length > 1 ? suffixWords(parts[parts.length - 1]) : null;
    if (!tail) break;
    noteSuffixes(tail);
    parts.pop();
  }
  if (parts.length > 2) return null;
  if (parts.length === 2) {
    const lastNameWords = parts[0].split(/\s+/);
    // "Smith, John" / "de Souza, Maria": the part before the comma must be a
    // surname. "Sarah Jones, Acme" is a person plus a company, not Last, First.
    const isSurname = lastNameWords.length === 1
      || lastNameWords.slice(0, -1).every((w) => PARTICLES.indexOf(w.toLowerCase()) >= 0);
    if (!isSurname) return null;
    s = `${parts[1]} ${parts[0]}`; // "Smith, John" -> "John Smith"
  } else {
    s = parts[0];
  }

  let tokens = s.split(/\s+/).map((t) => t.replace(/\.+$/, '')).filter(Boolean);
  while (tokens.length > 1) {
    const tail = suffixWords(tokens[tokens.length - 1]);
    if (!tail) break;
    noteSuffixes(tail); // "John Smith Jr" / "John Smith PhD"
    tokens = tokens.slice(0, -1);
  }
  let sawHonorific = false;
  while (tokens.length > 0 && HONORIFICS.indexOf(tokens[0].toLowerCase()) >= 0) {
    tokens = tokens.slice(1);
    sawHonorific = true;
  }
  // "Dr Smith": what is left is a surname, and "Hi Smith" is the wrong guess.
  if (sawHonorific && tokens.length < 2) return null;
  if (tokens.length === 0 || tokens.length > 4) return null;
  if (!tokens.every((t) => NAME_TOKEN_RE.test(t))) return null;
  // "Joe's Pizza", "Jones' Plumbing": a possessive is a business, not a person.
  if (tokens.some((t) => /['\u2019]s?$/i.test(t))) return null;
  if (tokens.some((t) => {
    const k = t.toLowerCase();
    // An honorific left in the middle ("Mr and Mrs Smith" once "Mr" is off) means a couple, not one name.
    return NOT_A_PERSON.indexOf(k) >= 0 || FUNCTION_WORDS.indexOf(k) >= 0 || HONORIFICS.indexOf(k) >= 0;
  })) return null;
  if (tokens[0].length < 2) return null; // a lone initial

  // A particle typed lowercase inside the name stays as typed: re-casing it would
  // turn "Pieter van der Berg" into "Pieter Van Der Berg" in a formal greeting.
  const lastIdx = tokens.length - 1;
  const tidy = tokens.map((t, i) => (i > 0 && i < lastIdx && PARTICLES.indexOf(t) >= 0 ? t : tidyNameToken(t)));
  return { first: tidy[0], full: tidy.concat(generational ? [generational] : []).join(' ') };
}

// Role-ish words that only show up in mailbox names, plus the joining words
// ("the.smiths@", "smith.family@") that are not a first name either.
const LOCAL_PART_ROLES = NOT_A_PERSON.concat(FUNCTION_WORDS, ['front', 'no', 'reply', 'do', 'not']);

/**
 * Last-resort first name from `first.last@...`. Only that shape: `jsmith@` or
 * `ben@` give no way to tell a first name from a handle, so they stay unresolved.
 */
export function firstNameFromEmail(email: unknown): string | null {
  const local = String(email ?? '').split('@')[0].split('+')[0].trim().toLowerCase();
  if (!local) return null;
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length < 2 || parts.length > 3) return null;
  if (!parts.every((p) => /^[a-z]+$/.test(p))) return null;
  if (parts.some((p) => LOCAL_PART_ROLES.indexOf(p) >= 0)) return null;
  if (parts[0].length < 2) return null;
  return tidyNameToken(parts[0]);
}

/** "John Smith <j@x.com>", `"Smith, John" <j@x.com>` or a bare address. */
export function parseRecipient(raw: unknown): { name: string | null; email: string | null } {
  const s = String(raw ?? '').trim();
  if (!s) return { name: null, email: null };
  const m = /^\s*(?:"([^"]*)"|([^<]*?))\s*<([^>]+)>\s*$/.exec(s);
  if (m) {
    const name = (m[1] !== undefined ? m[1] : m[2] || '').trim();
    const email = m[3].trim();
    return { name: name || null, email: email.indexOf('@') > 0 ? email : null };
  }
  return { name: null, email: s.indexOf('@') > 0 ? s : null };
}

// -- Context --------------------------------------------------------------

export interface SnippetContext {
  /** Display name the composer knows for the primary recipient (validated by personName at resolve time). */
  recipientName?: string | null;
  /** Primary recipient's address; only feeds the conservative first.last fallback. */
  recipientEmail?: string | null;
  /** The linked customer's name, when the composer knows one. */
  customerName?: string | null;
  /** The signed-in sender's first name. */
  myFirstName?: string | null;
  /** Injectable clock for tests. */
  now?: Date;
}

export interface CustomerLike {
  id?: string;
  name?: string;
  company?: string;
  email?: string;
  contacts?: Array<{ name?: string; email?: string }>;
}

const lower = (v: unknown): string => String(v ?? '').trim().toLowerCase();
const clean = (v: unknown): string => String(v ?? '').replace(/\s+/g, ' ').trim();

/**
 * Gather what the composer knows about the recipient. Name sources, most to
 * least specific, first one that passes personName wins:
 *   1. the To chip's own display name,
 *   2. the name the replied-to message came from (same address),
 *   3. a contact of a customer with that address,
 *   4. the customer record whose primary email it is (its `name` is the
 *      person's; skipped when it merely repeats the company).
 *
 * A message to SEVERAL people has no single first name: greeting all of them as
 * the first one leaves the others out, so firstName / fullName stay a visible gap
 * for the owner to write ("Hi Sam and Tom").
 */
export function buildSnippetContext(args: {
  to: string[];
  replyFrom?: Array<{ address?: string; name?: string }> | null;
  customers?: CustomerLike[] | null;
  customerId?: string | null;
  myFirstName?: string | null;
  now?: Date;
}): SnippetContext {
  const recipients = (args.to || []).map(parseRecipient).filter((r) => r.email || r.name);
  const distinct: string[] = [];
  recipients.forEach((r) => {
    const key = lower(r.email) || lower(r.name);
    if (distinct.indexOf(key) < 0) distinct.push(key);
  });
  const several = distinct.length > 1;
  const primary = recipients[0] || { name: null, email: null };
  const email = lower(primary.email);
  const customers = args.customers || [];

  let recipientName: string | null = null;
  if (!several) {
    const candidates: Array<string | null | undefined> = [primary.name];
    if (email) {
      (args.replyFrom || []).forEach((f) => { if (lower(f?.address) === email) candidates.push(f?.name); });
      customers.forEach((c) => (c.contacts || []).forEach((ct) => {
        if (lower(ct?.email) === email) candidates.push(ct?.name);
      }));
      customers.forEach((c) => {
        if (lower(c.email) === email && lower(c.name) !== lower(c.company)) candidates.push(c.name);
      });
    }
    recipientName = candidates.find((n) => personName(n) !== null) || null;
  }

  // The customer an address belongs to; two customers sharing it: do not guess.
  const customerFor = (addr: string): CustomerLike | null => {
    if (!addr) return null;
    const matches = customers.filter((c) => lower(c.email) === addr
      || (c.contacts || []).some((ct) => lower(ct?.email) === addr));
    return matches.length === 1 ? matches[0] : null;
  };
  let customer = args.customerId ? customers.find((c) => c.id === args.customerId) : undefined;
  if (!customer) {
    // Every recipient has to point at the same customer, or none is named.
    const matched = recipients.map((r) => customerFor(lower(r.email)));
    if (matched.length > 0 && matched.every((c) => c !== null && c === matched[0])) customer = matched[0] || undefined;
  }

  return {
    recipientName,
    recipientEmail: several ? null : primary.email,
    customerName: clean(customer?.name) || null,
    myFirstName: clean(args.myFirstName) || null,
    now: args.now,
  };
}

// -- Resolution -----------------------------------------------------------

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];

/**
 * Australian long date in ADELAIDE time: "30 September 2026". The business runs on
 * Adelaide dates (see dateUtils), so a laptop on a travel / VPN time zone must not
 * send a quote dated yesterday. Only the numeric day / month / year are taken from
 * Intl (never its order or month names, which small-ICU builds get wrong).
 */
export function formatAuDate(d: Date): string {
  try {
    const parts = new Intl.DateTimeFormat('en-AU', {
      timeZone: ADELAIDE_TZ, day: 'numeric', month: 'numeric', year: 'numeric',
    }).formatToParts(d);
    const num = (type: string): number => Number(parts.find((p) => p.type === type)?.value);
    const day = num('day');
    const month = num('month');
    const year = num('year');
    if (day >= 1 && month >= 1 && month <= 12 && year > 0) return `${day} ${MONTHS[month - 1]} ${year}`;
  } catch { /* no Intl / time-zone data: fall back to the device's own date below */ }
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function snippetVariableValues(ctx: SnippetContext): Record<SnippetVariable, string | null> {
  const person = personName(ctx.recipientName);
  return {
    firstName: person ? person.first : firstNameFromEmail(ctx.recipientEmail),
    fullName: person ? person.full : null,
    customerName: clean(ctx.customerName) || null,
    myFirstName: clean(ctx.myFirstName) || null,
    today: formatAuDate(ctx.now || new Date()),
  };
}

// A whole tag, attribute values included: a quoted value may hold a raw ">", so
// `<[^>]*>` would cut such a tag short and leave its tail behind as "text".
const TAG_SOURCE = '<\\/?[A-Za-z!](?:"[^"]*"|\'[^\']*\'|[^>"\'])*>';
const TAG_SPLIT_RE = new RegExp(`(${TAG_SOURCE})`);
// A quoted attribute, for the href filler: (name and equals sign)(double-quoted | single-quoted value).
const QUOTED_ATTR_SOURCE = '(\\s[^\\s"\'<>/=]+\\s*=\\s*)(?:"([^"]*)"|\'([^\']*)\')';

const urlEncode = (value: string): string | null => {
  try { return encodeURIComponent(value); } catch { return null; } // a lone surrogate throws
};

/**
 * Fill {{variables}} in snippet HTML. Values are HTML-escaped (they come from
 * email headers, i.e. from strangers). Text is filled as-is. The ONLY attribute
 * that is ever filled is a link's href, and there the value is URL-encoded, so it
 * can neither break out of the attribute nor change the URL's structure. A
 * variable that is unknown, malformed or has no confident value stays as a visible
 * literal (highlighted in text, left in place in an href) and is listed in
 * `unresolved` (canonical spelling for the known ones).
 */
export function resolveSnippetVariables(html: string, ctx: SnippetContext): { html: string; unresolved: string[] } {
  const values = snippetVariableValues(ctx);
  const fillText = (text: string): string => text.replace(new RegExp(TOKEN_PATTERN, 'g'), (m: string, inner: string) => {
    const tok = classifyToken(inner);
    const value = tok.canon ? values[tok.canon] : null;
    if (value) return escapeHtml(value);
    // A malformed token is shown as typed (this text is already HTML); only a stray < or > is
    // neutralised, so whatever sits inside the braces can never open a tag inside the highlight.
    const shown = tok.wellFormed ? `{{${escapeHtml(tok.name)}}}` : m.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<mark class="snippet-var" data-snippet-var="${escapeHtml(tok.name)}">${shown}</mark>`;
  });
  const fillTag = (tag: string): string => tag.replace(new RegExp(QUOTED_ATTR_SOURCE, 'g'), (m: string, head: string, dq?: string, sq?: string) => {
    if (!/^\s*href\s*=/i.test(head)) return m; // every other attribute is left exactly as it is
    const quote = dq !== undefined ? '"' : "'";
    const filled = String(dq !== undefined ? dq : sq).replace(new RegExp(TOKEN_PATTERN, 'g'), (tokenText: string, inner: string) => {
      const tok = classifyToken(inner);
      const value = tok.canon ? values[tok.canon] : null;
      const encoded = value ? urlEncode(value) : null;
      return encoded === null ? tokenText : escapeHtml(encoded);
    });
    return `${head}${quote}${filled}${quote}`;
  });
  const out = String(html ?? '')
    .split(TAG_SPLIT_RE)
    .map((part, i) => (i % 2 === 1 ? fillTag(part) : fillText(part)))
    .join('');
  // Read back from the result with the very scan the send guard uses, so what is
  // reported here is exactly what the guard will later refuse.
  return { html: out, unresolved: findUnresolvedVariables(out) };
}

// Cheap pre-check (bodies can hold MBs of pasted images): no brace pair and no
// percent-encoded brace anywhere means there is nothing to look for.
const BRACE_HINT_RE = /\{\{|\}\}|%7[bd]/i;

/** Report every token in `text` (each occurrence): every {{...}} span, then leftover brace pairs as typed. */
function collectTokens(text: string, onToken: (name: string) => void): void {
  const rest = text.replace(new RegExp(TOKEN_PATTERN, 'g'), (_m: string, inner: string) => {
    onToken(classifyToken(inner).name);
    return '\u0002';
  });
  // What is still a brace pair now was never a well-formed token: "{{firstName"
  // (never closed), "firstName}}" / "{firstName}}" (never opened). Shown with up to
  // 30 characters of the word beside it, so the owner can find it. Located with a
  // plain scan for the pair (not a regex that tries the word at every position):
  // a body holding megabytes of unbroken text must not cost 30 steps per character.
  const pair = /\{\{|\}\}/g;
  const isWordChar = (ch: string): boolean => !/\s/.test(ch) && '{}\u0001\u0002'.indexOf(ch) < 0; // not a space, brace or marker
  let m: RegExpExecArray | null;
  while ((m = pair.exec(rest)) !== null) {
    let shown: string;
    if (m[0] === '{{') {
      let end = m.index + 2;
      while (end < rest.length && end - m.index - 2 < 30 && isWordChar(rest.charAt(end))) end += 1;
      // The sentence's own punctuation after an unclosed "{{firstName," is not part of the token.
      shown = rest.slice(m.index, end).replace(/[.,;:!?)\]'"]+$/, '');
    } else {
      let start = m.index;
      while (start > 0 && m.index - start < 30 && isWordChar(rest.charAt(start - 1))) start -= 1;
      if (start > 0 && rest.charAt(start - 1) === '{') start -= 1; // "{firstName}}": only one brace opened it
      shown = rest.slice(start, m.index + 2);
    }
    onToken(shown);
  }
}

/** Every attribute value in `html`, separated by a control char, with percent-encoded braces decoded (a link can hide "%7B%7BfirstName%7D%7D"). */
function attributeText(html: string): string {
  const values: string[] = [];
  (html.match(new RegExp(TAG_SOURCE, 'g')) || []).forEach((tag) => {
    if (tag.charAt(1) === '/') return; // a closing tag has none
    const attr = /[^\s"'<>/=]+\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'<>`=]+))/g;
    let m: RegExpExecArray | null;
    while ((m = attr.exec(tag)) !== null) {
      const v = m[1] ?? m[2] ?? m[3] ?? '';
      // Only a value that can hold a brace pair is worth decoding (a pasted image is one huge data: URL).
      if (v && BRACE_HINT_RE.test(v)) values.push(v.replace(/%7B/gi, '{').replace(/%7D/gi, '}'));
    }
  });
  return values.join('\u0001');
}

/** The one scan behind the send guard, the amber note and the counts: `onToken` gets every placeholder occurrence in `html`. */
function scanPlaceholders(html: string, onToken: (name: string) => void): void {
  const s = String(html ?? '');
  if (!BRACE_HINT_RE.test(s)) return;
  // Comments never reach the reader (the editor drops them, a mail client does not draw them).
  const visible = s.replace(/<!--[\s\S]*?-->/g, '');
  // A block boundary becomes a control char, not a space: the token pattern tolerates
  // whitespace inside the braces, so a space would let `{{` and `name}}` in two
  // different paragraphs read as one variable.
  const text = visible
    .replace(/<(?:br|\/p|\/div|\/li|\/h[1-6])\b[^>]*>/gi, '\u0001')
    .replace(new RegExp(TAG_SOURCE, 'g'), '')
    .replace(/&nbsp;/gi, ' ');
  collectTokens(text, onToken);
  collectTokens(attributeText(visible), onToken);
}

/**
 * Every placeholder still in `html`, deduplicated, for the send guard. A placeholder
 * is ANYTHING that looks like one, not just a well-formed {{name}}: "{{first name}}",
 * "{{customer.name}}", "{{{firstName}}}", "{{firstName}" and a bare "}}" all go to
 * the client as literal braces if nobody notices. Well-formed names come back bare
 * ("firstName"; known ones in canonical spelling), everything else as typed, braces
 * included - `unfilledTokenText` turns either into display text.
 *
 * Reads the visible text (so `{{first<b>Name</b>}}` or a wrapped highlight still
 * counts) AND attribute values: a `{{token}}` left in a link's href is invisible in
 * the editor yet is what the client's click actually opens.
 */
export function findUnresolvedVariables(html: string): string[] {
  const found: string[] = [];
  scanPlaceholders(html, (name) => { if (found.indexOf(name) < 0) found.push(name); });
  return found;
}

/**
 * How often each placeholder occurs, named exactly as findUnresolvedVariables names
 * it (same scan, so the two can never disagree about what a placeholder is). For the
 * composer's quoted-thread baseline. The result has NO prototype: a plain `{}` would
 * answer `counts.constructor` with a function and swallow `counts.__proto__ = n`,
 * hiding exactly the placeholders that are named like Object.prototype members.
 */
export function countUnresolvedVariables(html: string): Record<string, number> {
  const counts: Record<string, number> = Object.create(null);
  scanPlaceholders(html, (name) => { counts[name] = (counts[name] || 0) + 1; });
  return counts;
}

/**
 * Placeholders that are NOT one of our variables (typos, made-up names, stray
 * braces). For the snippet form: a snippet saved with "{{first name}}" can only
 * ever produce a gap, so say so when it is written, not when a client is waiting.
 */
export function findUnknownVariables(html: string): string[] {
  return findUnresolvedVariables(html).filter((v) => !IDENTIFIER_RE.test(v) || canonicalVariable(v) === null);
}

/** One placeholder as the owner should see it: a bare name gets its braces, a literal token is shown as typed. */
export function unfilledTokenText(v: string): string {
  return /[{}]/.test(v) ? v : `{{${v}}}`;
}

/**
 * The composer's refusal while placeholders are left in the owner's own text.
 * Names every variable so the owner knows exactly what to replace.
 */
export function describeUnfilledVariables(vars: string[]): string {
  const list = vars.map(unfilledTokenText).join(', ');
  const many = vars.length > 1;
  return `can't send yet - ${many ? 'these snippet fields are' : 'this snippet field is'} still unfilled: ${list}. `
    + `Replace ${many ? 'them' : 'it'} with the real text first.`;
}

// What a snippet may put in the editor - and so, in the email. The form only ever
// produces paragraphs, line breaks and links; anything richer is text formatting.
// Deliberately NOT images: an <img> in a snippet is fetched by the owner's browser
// the moment it renders and is mailed to the client, i.e. a tracking pixel or a
// spoofed banner planted by whoever can write the snippet. No class / target / rel /
// style either - the editor sets its own link attributes.
const INSERT_TAGS = ['p', 'br', 'div', 'span', 'a', 'strong', 'b', 'em', 'i', 'u', 's', 'ul', 'ol', 'li',
  'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'mark'];
const INSERT_ATTRS = ['href', 'data-snippet-var'];

/**
 * Snippet HTML ready for the editor: sanitised, variables filled, then reduced to
 * the INSERT_TAGS allowlist (the second pass also covers the filled values), and
 * stripped of <hr> - an <hr> is the composer's "quoted thread starts here" anchor,
 * so one in the owner's own text would hide everything after it from the send guard.
 */
export function renderSnippetForInsert(bodyHtml: string, ctx: SnippetContext): { html: string; unresolved: string[] } {
  const safe = sanitizeHtml(bodyHtml, { noStyleTags: true });
  const { html } = resolveSnippetVariables(safe, ctx);
  const final = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: INSERT_TAGS,
    ALLOWED_ATTR: INSERT_ATTRS,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  }).replace(/<hr\b[^>]*>/gi, '');
  return { html: final, unresolved: findUnresolvedVariables(final) };
}

// -- Text <-> HTML (the snippet form edits plain text) -----------------------

const decodeEntities = (s: string): string => s
  .replace(/&nbsp;/gi, ' ')
  .replace(/&lt;/gi, '<')
  .replace(/&gt;/gi, '>')
  .replace(/&quot;/gi, '"')
  .replace(/&(?:#39|#x27|apos);/gi, "'")
  .replace(/&#(\d+);/g, (_m, n: string) => { const c = parseInt(n, 10); return c > 0 && c <= 0x10ffff ? String.fromCodePoint(c) : ''; })
  .replace(/&amp;/gi, '&');

/** Readable plain text from HTML: paragraph breaks kept, links written as "text (url)". */
export function htmlToPlainText(html: string): string {
  let s = String(html ?? '').replace(/\r?\n/g, ' ');
  s = s.replace(/<a\b[^>]*?\bhref\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi, (_m, href: string, inner: string) => {
    const label = decodeEntities(inner.replace(/<[^>]*>/g, '')).trim();
    const url = decodeEntities(href).trim();
    if (!/^(https?:|mailto:|tel:)/i.test(url)) return inner;
    if (!label) return url;
    // An auto-linked address or number is its own label: "bob@x.com", not
    // "bob@x.com (mailto:bob@x.com)" (which "save message as snippet" would then store).
    const bare = url.replace(/^(?:mailto|tel):/i, '');
    const sameAsLabel = label === url || label === bare
      || (/^tel:/i.test(url) && label.replace(/\s+/g, '') === bare.replace(/\s+/g, ''));
    return sameAsLabel ? label : `${label} (${url})`;
  });
  s = s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|h[1-6]|blockquote|ul|ol|pre|tr)>/gi, '\n\n')
    .replace(/<li\b[^>]*>/gi, '- ')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]*>/g, '');
  return decodeEntities(s)
    .split('\n')
    .map((line) => line.replace(/[ \t]+$/, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Trailing punctuation belongs to the sentence, not the link - except the closing
 * braces of a {{variable}} at the very end of the URL, which are part of it.
 */
function trimUrlEnd(raw: string): string {
  let url = raw;
  for (;;) {
    if (/\{\{[^{}]*\}\}$/.test(url)) break;
    const next = url.replace(/[.,;:!?)\]}]$/, '');
    if (next === url) break;
    url = next;
  }
  return url;
}

/**
 * Plain text (what the owner types in the snippet form) -> HTML: blank lines
 * become paragraphs, single newlines <br>, bare http(s) URLs become links. Text
 * is escaped first, so nothing typed in the form can ever be markup.
 */
export function plainTextToHtml(text: string): string {
  const src = String(text ?? '').replace(/\r\n?/g, '\n').trim();
  if (!src) return '';
  const inline = (line: string): string => {
    let out = '';
    let last = 0;
    const re = /https?:\/\/[^\s<>"']+/g;
    let m: RegExpExecArray | null;
    while ((m = re.exec(line)) !== null) {
      const url = trimUrlEnd(m[0]);
      if (!url) continue;
      out += escapeHtml(line.slice(last, m.index));
      out += `<a href="${escapeHtml(url)}">${escapeHtml(url)}</a>`;
      last = m.index + url.length;
      re.lastIndex = last;
    }
    return out + escapeHtml(line.slice(last));
  };
  return src
    .split(/\n{2,}/)
    .map((para) => `<p>${para.split('\n').map(inline).join('<br>')}</p>`)
    .join('');
}
